import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

/**
 * @fileoverview Utilitários para formatação de datas no padrão brasileiro (pt-BR).
 * Este módulo fornece funções para converter e formatar datas em diferentes formatos,
 * utilizando principalmente as APIs nativas do JavaScript e a biblioteca date-fns.
 */

/**
 * Formata uma data em string no formato padrão brasileiro (dd/mm/aaaa).
 *
 * Utiliza a API nativa `Intl.DateTimeFormat` para garantir a formatação correta
 * no padrão brasileiro, independentemente da localização do sistema.
 *
 * @param date - Data em formato string (aceita qualquer formato compatível com `new Date()`).
 *               Exemplos: "2025-07-28", "2025/07/28", "Jul 28, 2025", etc.
 * @returns Data formatada como string no formato "dd/mm/aaaa".
 *
 * @example
 * formatDate("2025-07-28");
 * // Retorna: "28/07/2025"
 *
 * @example
 * formatDate("Jul 28, 2025");
 * // Retorna: "28/07/2025"
 *
 * @throws {Error} Quando a string fornecida não pode ser convertida para uma data válida.
 */
export const formatDate = (date: string): string => {
  const dateFormatted = new Intl.DateTimeFormat("pt-BR").format(new Date(date));

  return dateFormatted;
};

/**
 * Formata uma data em string para o formato especificado usando date-fns.
 *
 * Esta função oferece flexibilidade para formatar datas em diversos padrões
 * utilizando as máscaras de formatação da biblioteca date-fns.
 *
 * @param date - Data em formato string (deve ser compatível com `new Date()`).
 * @param dateFormat - Máscara de formatação da data seguindo os padrões do date-fns.
 *                     Exemplos: "dd/MM/yyyy", "yyyy-MM-dd", "dd 'de' MMMM 'de' yyyy", etc.
 * @returns Data formatada conforme o padrão fornecido ou string vazia se `date` for inválida ou vazia.
 *
 * @example
 * newFormatDate("2025-07-28", "dd/MM/yyyy");
 * // Retorna: "28/07/2025"
 *
 * @example
 * newFormatDate("2025-07-28", "yyyy-MM-dd");
 * // Retorna: "2025-07-28"
 *
 * @example
 * newFormatDate("", "dd/MM/yyyy");
 * // Retorna: ""
 *
 * @see {@link https://date-fns.org/v2.29.3/docs/format} Documentação das máscaras de formatação
 */
export const newFormatDate = (date: string, dateFormat: string): string => {
  if (date) {
    return format(new Date(date), dateFormat);
  } else {
    return "";
  }
};

/**
 * Formata uma data do tipo `Date` para o formato completo em português do Brasil,
 * incluindo data e hora no padrão descritivo brasileiro.
 *
 * Converte um objeto Date para uma string legível em português, seguindo o formato:
 * "dd de mês de yyyy às HH:mm" (ex: "28 de julho de 2025 às 10:00").
 *
 * @param date - Objeto Date a ser formatado. Deve ser uma instância válida de Date.
 * @returns String formatada com data e hora em português brasileiro ou string vazia se a data for inválida/null/undefined.
 *
 * @example
 * formatDateAndTimeToString(new Date("2025-07-28T10:30:00"));
 * // Retorna: "28 de julho de 2025 às 10:30"
 *
 * @example
 * formatDateAndTimeToString(new Date("2025-12-31T23:59:00"));
 * // Retorna: "31 de dezembro de 2025 às 23:59"
 *
 * @example
 * formatDateAndTimeToString(null);
 * // Retorna: ""
 */
export const formatDateAndTimeToString = (date: Date): string => {
  if (!date) return "";

  return format(new Date(date), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
    locale: ptBR
  });
};

/**
 * Converte uma data no formato brasileiro "dd/mm/aaaa" em um objeto contendo
 * o mês por extenso (capitalizado) e o ano como número.
 *
 * Esta função é útil para exibir informações de data de forma mais legível,
 * especialmente em interfaces de usuário que precisam mostrar o mês por extenso.
 *
 * @param dateStr - Data em formato brasileiro "dd/mm/aaaa".
 *                  Deve seguir exatamente este padrão para funcionar corretamente.
 * @returns Objeto contendo:
 *          - `month`: String com o nome do mês por extenso, com a primeira letra maiúscula
 *          - `year`: Número representando o ano
 *
 * @example
 * parseDateToMonthYear("15/07/2025");
 * // Retorna: { month: "Julho", year: 2025 }
 *
 * @example
 * parseDateToMonthYear("01/01/2024");
 * // Retorna: { month: "Janeiro", year: 2024 }
 *
 * @example
 * parseDateToMonthYear("31/12/2023");
 * // Retorna: { month: "Dezembro", year: 2023 }
 *
 * @throws {Error} Quando o formato da string não corresponde a "dd/mm/aaaa" ou contém valores inválidos.
 */
export function parseDateToMonthYear(dateStr: string): {
  month: string;
  year: number;
} {
  const [day, month, year] = dateStr.split("/").map(Number);
  const date = new Date(year, month - 1, day);

  const rawMonth = new Intl.DateTimeFormat("pt-BR", { month: "long" }).format(
    date
  );
  const capitalizedMonth = rawMonth.charAt(0).toUpperCase() + rawMonth.slice(1);

  return {
    month: capitalizedMonth,
    year: year
  };
}

/**
 * Formata um objeto `Date` para o formato curto de data e médio de hora
 * no padrão brasileiro (pt-BR).
 *
 * Utiliza a API nativa `Intl.DateTimeFormat` com as opções `dateStyle: "short"`
 * e `timeStyle: "medium"` para produzir uma formatação consistente e localizada.
 * O resultado segue o padrão: "dd/mm/aaaa hh:mm:ss".
 *
 * @param date - Objeto `Date` a ser formatado. Deve ser uma instância válida de Date.
 * @returns String contendo a data e hora formatadas no padrão "dd/mm/aaaa hh:mm:ss".
 *
 * @example
 * formatDateTimeShort(new Date("2025-07-28T10:35:12"));
 * // Retorna: "28/07/2025 10:35:12"
 *
 * @example
 * formatDateTimeShort(new Date("2025-12-31T23:59:59"));
 * // Retorna: "31/12/2025 23:59:59"
 *
 * @example
 * formatDateTimeShort(new Date());
 * // Retorna a data e hora atuais no formato: "28/07/2025 15:30:45"
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat} Documentação do Intl.DateTimeFormat
 */
export function formatDateTimeShort(date: Date): string {
  const dateTimeFormatter = new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium"
  });

  return dateTimeFormatter.format(date);
}
